import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Autocomplete,
  FormControl,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { tournamentService } from '../../services/tournamentService';
import { tournamentNameService } from '../../services/tournamentNameService';
import { teamNameService } from '../../services/teamNameService';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TournamentNameModal from './TournamentNameModal';
import TeamNameModal from './TeamNameModal';

const CATEGORIES = [
  'League of Legends',
  'VALORANT',
  'Apex Legends',
  'PUBG',
  'Fortnite',
  'Overwatch 2',
  'Street Fighter 6',
  'FIFA 24',
  'Call of Duty',
  'Dota 2',
  'Counter-Strike 2',
  "イベント",
  'その他',
];

const PLATFORMS = [
  'YouTube',
  'Twitch',
  'ニコニコ生放送',
  'OPENREC',
  'その他',
];

const BO_FORMATS = [
  { value: '', label: 'なし' },
  { value: 'BO1', label: 'BO1' },
  { value: 'BO3', label: 'BO3' },
  { value: 'BO5', label: 'BO5' },
  { value: 'BO7', label: 'BO7' },
];

const TournamentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tournamentNames, setTournamentNames] = useState([]);
  const [loadingNames, setLoadingNames] = useState(true);
  const [teamNames, setTeamNames] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  
  // モーダルの状態管理
  const [tournamentNameModalOpen, setTournamentNameModalOpen] = useState(false);
  const [teamNameModalOpen, setTeamNameModalOpen] = useState(false);
  const [teamModalIndex, setTeamModalIndex] = useState(null);

  const { register, control, handleSubmit, reset, formState: { errors }, setValue, watch, getValues } = useForm({
    defaultValues: {
      tournamentName: '',
      date: '',
      startTime: '',
      endTime: '',
      category: '',
      venue: '',
      venueLocation: '',
      matchups: [{ round: '', teams: [{ name: '' }, { name: '' }], boFormat: '' }],
      broadcast: [{ platform: '', url: '' }],
      officialSite: '',
      isDraft: false // デフォルトは公開
    }
  });

  // 大会名とチーム名一覧の取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [names, teams] = await Promise.all([
          tournamentNameService.getAllTournamentNames(),
          teamNameService.getAllTeamNames()
        ]);
        setTournamentNames(names);
        setTeamNames(teams);
      } catch (error) {
        console.error('データの取得に失敗:', error);
        setError('データの取得に失敗しました。');
      } finally {
        setLoadingNames(false);
        setLoadingTeams(false);
      }
    };

    fetchData();
  }, []);

  // 時間文字列をDateオブジェクトに変換
  const parseTimeString = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // 編集時のみ大会情報を取得
  useEffect(() => {
    if (!isEditing) return;

    const fetchTournament = async () => {
      setLoading(true);
      try {
        const tournament = await tournamentService.getTournament(id);
        if (tournament) {
          // time フィールドを startTime と endTime に分割
          if (tournament.time) {
            const [startStr, endStr] = tournament.time.split(' - ');
            tournament.startTime = parseTimeString(startStr);
            tournament.endTime = parseTimeString(endStr);
            delete tournament.time;
          }
          reset(tournament);
        }
      } catch (err) {
        console.error('大会情報の取得に失敗:', err);
        setError('大会情報の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchTournament();
  }, [id, isEditing, reset]);

  const { fields: matchupFields, append: appendMatchup, remove: removeMatchup } = useFieldArray({
    control,
    name: 'matchups'
  });

  const { fields: broadcastFields, append: appendBroadcast, remove: removeBroadcast } = useFieldArray({
    control,
    name: 'broadcast'
  });

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (data) => {
    setSubmitting(true);
    setError(null);
    
    try {
      // startTime と endTime から time フィールドを生成
      let formattedStartTime = '';
      let formattedEndTime = '';
      
      if (data.startTime) {
        const startDate = new Date(data.startTime);
        formattedStartTime = `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`;
      }
      
      if (data.endTime) {
        const endDate = new Date(data.endTime);
        formattedEndTime = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
      }
      
      const submitData = {
        ...data,
        time: formattedStartTime && formattedEndTime ? `${formattedStartTime} - ${formattedEndTime}` : formattedStartTime || '',
        category: data.category || '', // カテゴリが確実に保存されるように
      };
      delete submitData.startTime;
      delete submitData.endTime;

      console.log('送信データ:', submitData); // デバッグ用

      if (isEditing) {
        await tournamentService.updateTournament(id, submitData);
        console.log('大会情報を更新しました');
      } else {
        const newId = await tournamentService.createTournament(submitData);
        console.log('新しい大会を作成しました:', newId);
      }
      navigate('/admin/tournaments');
    } catch (error) {
      console.error('大会情報の保存に失敗:', error);
      setError('大会情報の保存に失敗しました。もう一度お試しください。');
      setSubmitting(false);
    }
  };

  // 大会名登録成功時の処理
  const handleTournamentNameSuccess = (newTournament) => {
    // 現在のフォーム値を保存
    const currentValues = getValues();
    
    // 新しい大会名をリストに追加
    setTournamentNames([...tournamentNames, newTournament]);
    
    // フォーム値を復元し、新しい大会名を設定
    reset({
      ...currentValues,
      tournamentName: newTournament.name
    });
  };

  // チーム名登録成功時の処理
  const handleTeamNameSuccess = (newTeam) => {
    // 現在のフォーム値を保存
    const currentValues = getValues();
    
    // 新しいチーム名をリストに追加
    setTeamNames([...teamNames, newTeam]);
    
    if (teamModalIndex !== null) {
      const [matchupIndex, teamIndex] = teamModalIndex;
      
      // フォーム値を復元し、新しいチーム名を設定
      const updatedValues = {
        ...currentValues,
        matchups: [...currentValues.matchups]
      };
      updatedValues.matchups[matchupIndex].teams[teamIndex].name = newTeam.name;
      
      reset(updatedValues);
      setTeamModalIndex(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/admin/tournaments')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {isEditing ? 'トーナメント編集' : 'トーナメント新規作成'}
        </Typography>
      </Box>

      <Paper sx={{ 
        p: { xs: 3, md: 4 }, 
        mb: 4, 
        borderRadius: 0, 
        boxShadow: '0 0 30px rgba(0, 255, 255, 0.2)',
        border: '2px solid rgba(0, 255, 255, 0.3)',
        backgroundColor: 'rgba(37, 37, 48, 0.9)',
        backdropFilter: 'blur(10px)',
      }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {/* ステータス情報（編集時のみ表示） */}
            {isEditing && watch('createdFrom') === 'pandascore' && (
              <Alert severity="info" sx={{ mb: 3 }}>
                この大会はPandaScore APIから取得されました。
                {watch('isDraft') && ' 現在下書き状態です。'}
              </Alert>
            )}

            {/* 基本情報セクション */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#00ffff', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <Box sx={{ width: 4, height: 24, bgcolor: '#00ffff', mr: 2, borderRadius: 0, boxShadow: '0 0 10px #00ffff' }} />
                基本情報
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* ステータス切り替え（編集時のみ） */}
                {isEditing && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!watch('isDraft')}
                        onChange={(e) => setValue('isDraft', !e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#00ff00',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#00ff00',
                          },
                        }}
                      />
                    }
                    label={!watch('isDraft') ? '公開中' : '下書き'}
                    sx={{ mb: 2 }}
                  />
                )}
                <FormControl fullWidth error={!!errors.tournamentName}>
                  <Autocomplete
                    options={tournamentNames}
                    getOptionLabel={(option) => option?.name || ''}
                    loading={loadingNames}
                    disabled={loadingNames}
                    value={tournamentNames.find(t => t.name === watch('tournamentName')) || null}
                    onChange={(_, newValue) => {
                      setValue('tournamentName', newValue?.name || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="大会名"
                        error={!!errors.tournamentName}
                        helperText={errors.tournamentName?.message}
                        {...register('tournamentName', { required: '大会名は必須です' })}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#00ffff',
                            },
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
                
                <Button
                  onClick={() => setTournamentNameModalOpen(true)}
                  variant="outlined"
                  sx={{ 
                    py: 1.5,
                    borderColor: '#00ffff',
                    color: '#00ffff',
                    backgroundColor: 'rgba(0, 255, 255, 0.05)',
                    '&:hover': {
                      borderColor: '#66ffff',
                      bgcolor: 'rgba(0, 255, 255, 0.1)',
                      boxShadow: '0 0 15px rgba(0, 255, 255, 0.5)',
                    }
                  }}
                >
                  新しい大会名を登録
                </Button>
                
                <TextField
                  fullWidth
                  type="date"
                  label="開催日"
                  InputLabelProps={{ shrink: true }}
                  {...register('date', { 
                    required: '開催日は必須です',
                    validate: value => {
                      const selectedDate = new Date(value);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (selectedDate < today) {
                        alert('過去の日付が選択されています。この大会は過去のイベントとして登録されます。');
                      }
                      return true;
                    }
                  })}
                  error={!!errors.date}
                  helperText={errors.date?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#00ffff',
                      },
                    },
                  }}
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                    <TimePicker
                      label="開始時間"
                      value={watch('startTime') || null}
                      onChange={(newValue) => {
                        setValue('startTime', newValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.startTime,
                          helperText: errors.startTime?.message,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#00ffff',
                              },
                            },
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                  
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                    <TimePicker
                      label="終了時間"
                      value={watch('endTime') || null}
                      onChange={(newValue) => {
                        setValue('endTime', newValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.endTime,
                          helperText: errors.endTime?.message,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#00ffff',
                              },
                            },
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                
                <FormControl fullWidth error={!!errors.category}>
                  <Autocomplete
                    options={CATEGORIES}
                    value={watch('category') || null}
                    onChange={(_, newValue) => {
                      setValue('category', newValue || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="カテゴリー"
                        error={!!errors.category}
                        helperText={errors.category?.message}
                        {...register('category', { required: 'カテゴリーは必須です' })}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#00ffff',
                            },
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
                
                <TextField
                  fullWidth
                  label="会場"
                  {...register('venue', { required: '必須項目です' })}
                  error={!!errors.venue}
                  helperText={errors.venue?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#00ffff',
                      },
                    },
                  }}
                />
                
                <TextField
                  fullWidth
                  label="会場住所"
                  {...register('venueLocation', { required: '必須項目です' })}
                  error={!!errors.venueLocation}
                  helperText={errors.venueLocation?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#00ffff',
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* 対戦カードセクション */}
            <Box sx={{ mb: 5 }}>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#ff00ff', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <Box sx={{ width: 4, height: 24, bgcolor: '#ff00ff', mr: 2, borderRadius: 0, boxShadow: '0 0 10px #ff00ff' }} />
                対戦カード
              </Typography>
              {matchupFields.map((field, index) => (
                <Box key={field.id} sx={{ 
                  mb: 3, 
                  p: 3, 
                  border: '2px solid rgba(255, 0, 255, 0.2)', 
                  borderRadius: 0, 
                  backgroundColor: 'rgba(255, 0, 255, 0.02)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 0, 255, 0.05)',
                    borderColor: 'rgba(255, 0, 255, 0.4)',
                    boxShadow: '0 0 15px rgba(255, 0, 255, 0.3)',
                  }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#ffffff' }}>
                      対戦カード {index + 1}
                    </Typography>
                    {matchupFields.length > 1 && (
                      <IconButton 
                        onClick={() => removeMatchup(index)} 
                        sx={{ 
                          color: '#ef4444',
                          '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                      fullWidth
                      label="ラウンド"
                      {...register(`matchups.${index}.round`)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#ff00ff',
                          },
                        },
                      }}
                    />
                    
                    <FormControl fullWidth>
                      <Autocomplete
                        options={BO_FORMATS}
                        getOptionLabel={(option) => option.label}
                        value={BO_FORMATS.find(bo => bo.value === watch(`matchups.${index}.boFormat`)) || BO_FORMATS[0]}
                        onChange={(_, newValue) => {
                          setValue(`matchups.${index}.boFormat`, newValue?.value || '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="BO形式"
                            {...register(`matchups.${index}.boFormat`)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: '#ff00ff',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={teamNames}
                          getOptionLabel={(option) => option.name || ''}
                          loading={loadingTeams}
                          disabled={loadingTeams}
                          value={teamNames.find(t => t.name === watch(`matchups.${index}.teams.0.name`)) || null}
                          onChange={(_, newValue) => {
                            setValue(`matchups.${index}.teams.0.name`, newValue?.name || '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="チーム1"
                              {...register(`matchups.${index}.teams.0.name`)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: '#ff00ff',
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </FormControl>
                      
                      <Button
                        onClick={() => {
                          setTeamModalIndex([index, 0]);
                          setTeamNameModalOpen(true);
                        }}
                        variant="outlined"
                        size="small"
                        sx={{ 
                          py: 1,
                          borderColor: '#ff00ff',
                          color: '#ff00ff',
                          backgroundColor: 'rgba(255, 0, 255, 0.05)',
                          '&:hover': {
                            borderColor: '#ff66ff',
                            bgcolor: 'rgba(255, 0, 255, 0.1)',
                            boxShadow: '0 0 15px rgba(255, 0, 255, 0.5)',
                          }
                        }}
                      >
                        チーム1を新規登録
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={teamNames}
                          getOptionLabel={(option) => option.name || ''}
                          loading={loadingTeams}
                          disabled={loadingTeams}
                          value={teamNames.find(t => t.name === watch(`matchups.${index}.teams.1.name`)) || null}
                          onChange={(_, newValue) => {
                            setValue(`matchups.${index}.teams.1.name`, newValue?.name || '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="チーム2"
                              {...register(`matchups.${index}.teams.1.name`)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: '#ff00ff',
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </FormControl>
                      
                      <Button
                        onClick={() => {
                          setTeamModalIndex([index, 1]);
                          setTeamNameModalOpen(true);
                        }}
                        variant="outlined"
                        size="small"
                        sx={{ 
                          py: 1,
                          borderColor: '#ff00ff',
                          color: '#ff00ff',
                          backgroundColor: 'rgba(255, 0, 255, 0.05)',
                          '&:hover': {
                            borderColor: '#ff66ff',
                            bgcolor: 'rgba(255, 0, 255, 0.1)',
                            boxShadow: '0 0 15px rgba(255, 0, 255, 0.5)',
                          }
                        }}
                      >
                        チーム2を新規登録
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))}
              <Button
                type="button"
                startIcon={<AddIcon />}
                onClick={() => appendMatchup({ round: '', teams: [{ name: '' }, { name: '' }], boFormat: '' })}
                sx={{ 
                  mt: 2,
                  color: '#ff00ff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 0, 255, 0.1)',
                    filter: 'drop-shadow(0 0 8px #ff00ff)',
                  }
                }}
              >
                対戦カードを追加
              </Button>
            </Box>

            {/* 配信情報セクション */}
            <Box sx={{ mb: 5 }}>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#00ff00', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <Box sx={{ width: 4, height: 24, bgcolor: '#00ff00', mr: 2, borderRadius: 0, boxShadow: '0 0 10px #00ff00' }} />
                配信情報
              </Typography>
              {broadcastFields.map((field, index) => (
                <Box key={field.id} sx={{ 
                  mb: 3, 
                  p: 3, 
                  border: '2px solid rgba(0, 255, 0, 0.2)', 
                  borderRadius: 0, 
                  backgroundColor: 'rgba(0, 255, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 255, 0, 0.05)',
                    borderColor: 'rgba(0, 255, 0, 0.4)',
                    boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)',
                  }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#ffffff' }}>
                      配信 {index + 1}
                    </Typography>
                    {broadcastFields.length > 1 && (
                      <IconButton 
                        onClick={() => removeBroadcast(index)} 
                        sx={{ 
                          color: '#ef4444',
                          '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <FormControl fullWidth>
                      <Autocomplete
                        options={PLATFORMS}
                        value={watch(`broadcast.${index}.platform`) || null}
                        onChange={(_, newValue) => {
                          setValue(`broadcast.${index}.platform`, newValue || '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="プラットフォーム"
                            {...register(`broadcast.${index}.platform`)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: '#00ff00',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    
                    <TextField
                      fullWidth
                      label="URL"
                      {...register(`broadcast.${index}.url`)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#00ff00',
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
              <Button
                type="button"
                startIcon={<AddIcon />}
                onClick={() => appendBroadcast({ platform: '', url: '' })}
                sx={{ 
                  mt: 2,
                  color: '#00ff00',
                  '&:hover': {
                    bgcolor: 'rgba(0, 255, 0, 0.1)',
                    filter: 'drop-shadow(0 0 8px #00ff00)',
                  }
                }}
              >
                配信情報を追加
              </Button>
            </Box>

            {/* 公式サイトセクション */}
            <Box sx={{ mb: 5 }}>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#ffaa00', display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <Box sx={{ width: 4, height: 24, bgcolor: '#ffaa00', mr: 2, borderRadius: 0, boxShadow: '0 0 10px #ffaa00' }} />
                公式サイト
              </Typography>
              <TextField
                fullWidth
                label="URL"
                {...register('officialSite', {
                  pattern: {
                    value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                    message: '有効なURLを入力してください'
                  }
                })}
                error={!!errors.officialSite}
                helperText={errors.officialSite?.message}
                placeholder="https://example.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#ffaa00',
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="contained"
                type="submit"
                disabled={submitting}
                sx={{
                  position: 'relative',
                  minWidth: '150px',
                }}
              >
                {submitting ? (
                  <>
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        marginLeft: '-12px',
                      }}
                    />
                    送信中...
                  </>
                ) : (
                  isEditing ? '更新' : '作成'
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/tournaments')}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  }
                }}
              >
                キャンセル
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      {/* モーダル */}
      <TournamentNameModal
        open={tournamentNameModalOpen}
        onClose={() => setTournamentNameModalOpen(false)}
        onSuccess={handleTournamentNameSuccess}
      />
      <TeamNameModal
        open={teamNameModalOpen}
        onClose={() => {
          setTeamNameModalOpen(false);
          setTeamModalIndex(null);
        }}
        onSuccess={handleTeamNameSuccess}
      />
    </Box>
  );
};

export default TournamentForm;
